import {
  ColDef,
  FirstDataRenderedEvent,
  GridOptions,
  ModelUpdatedEvent,
  Module,
  RowDataChangedEvent,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  getFilterOnCellContextMenu,
  heliosClientSideRowConfig,
  heliosDefaultColWidth,
  heliosDefaultToolPanels,
} from '@helios/ag-grid-config'
import { formatDates } from '@helios/common-api'
import { SavedView } from '@helios/state-management'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import { prettyFormatNumber } from '@helios/utils'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { AgGridState, TableLayoutComponent } from '@northfork/ag-grid-angular'
import { composeFieldPath } from '@northfork/basics'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { DistributionsFactoryHistoryGridKey } from '../../../panel-key'
import { FactorHistory } from '../../models/factor-history.model'

const factorHistoryPath = composeFieldPath([] as FactorHistory[])
/**
 * Number formatter to 8 decimal place
 */
export function columnValueFormatter(value: unknown) {
  if (value === 0) {
    // Return itself just in case it's a zero
    return '0'
  }

  if (!value) {
    return ''
  }

  if (!isNaN(Number(value))) {
    return prettyFormatNumber(Number(value), 8)
  }

  return value
}

@Component({
  selector: 'hs-factor-history-grid[data]',
  templateUrl: './factor-history-grid.component.html',
})
export class FactorHistoryGridComponent implements OnDestroy, OnInit {
  @ViewChild('table')
  readonly table?: TableLayoutComponent<FactorHistory>
  private _filterState: { [columnName: string]: unknown } | undefined
  clearFiltersOnAccountLoadToggle = new FormControl(true)
  gridApi?: AgGridApi

  @Input() set savedViews(savedViewsStates: SavedView[] | null | undefined) {
    this._filterState = savedViewsStates?.find(
      view => view.gridKey === DistributionsFactoryHistoryGridKey,
    )?.savedView.filterState
  }

  @Input() data: FactorHistory[] | null = null

  readonly gridModules: Module[] = [FiltersToolPanelModule]
  readonly gridOptions: GridOptions = this.createGridOptions()
  private readonly destroySubject$ = new Subject<void>()

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}

  ngOnInit(): void {
    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
  }

  private createGridOptions(): GridOptions {
    return {
      columnDefs: this.createColumnDefs(),
      getContextMenuItems: params => getFilterOnCellContextMenu(params),
      ...heliosClientSideRowConfig,
      onRowDataChanged: event => this.onRowDataChanged(event),
      ...disableChangeDetectionOnScroll(this.changeDetector),
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: [
            {
              label: 'Clear Filters on Load',
              formControl: this.clearFiltersOnAccountLoadToggle,
            },
          ],
        }),
        ...heliosDefaultToolPanels,
      ]),
      onGridReady: async params => {
        enableHeliosCopyBehavior(params.api)
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
        this.gridApi = params
        /* why move this later ? so that all filter change events are called before workspace is rendered and only if workspace 
    has filter model it ever again calls filter model change not otherwise avoiding unnecessary race conditions */
        const agGridStateSaved = await this.savedViewService.manageSavedView(
          params,
          DistributionsFactoryHistoryGridKey,
          this.destroySubject$,
          true,
          this.getCurrentPanelSettingsObject(),
        )

        this.applyGridConfig(agGridStateSaved)
      },
      onModelUpdated: event =>
        event.newData && this._getAndApplyColumnFilter(event),
    }
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('clearFiltersOnAccountLoad' in panelSettings) {
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )
      }
    }
  }

  private _getAndApplyColumnFilter(
    event: ModelUpdatedEvent | FirstDataRenderedEvent,
  ): void {
    if (!event.api.isColumnFilterPresent()) {
      if (this._filterState) {
        // Apply preserved filters once grid is rendered
        event.api.setFilterModel(this._filterState)
        setTimeout(() => {
          event.api.onFilterChanged()
        })
      }
    }
  }

  // @internal
  createColumnDefs(): ColDef[] {
    return [
      {
        field: factorHistoryPath('securityId'),
        headerName: 'Security ID',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: factorHistoryPath('accountId'),
        headerName: 'Account ID',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: factorHistoryPath('date'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        field: factorHistoryPath('principalFactor'),
      },
      {
        field: factorHistoryPath('indexFactor'),
      },
      {
        field: factorHistoryPath('combinedFactor'),
      },
    ]
  }

  private onRowDataChanged(event: RowDataChangedEvent) {
    if (this.data != null) {
      return
    }
    event.api.showLoadingOverlay()
  }
}
